import { createElement, forwardRef } from 'react';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { useGetDirection } from '../../../locale/useBidiFormatter';
import { BaseTextProps } from './types';
import { useHighlightableText } from '../../../hooks/useHighlightableText';
import { useIsRTL } from '../../../locale';

import cx from 'classnames';
import '../Text.css';
import cn from '../Text.css.json';

export const BaseText = forwardRef<HTMLSpanElement, BaseTextProps>((props: BaseTextProps, ref) => {
  // destruct all props that not HTMLAttributes<HTMLSpanElement> except classname and styles
  const {
    testId,
    className,
    style,
    wrapText,
    preventTruncationTooltip,
    focusable,
    disabled,
    tokens,
    variant,
    ariaLabel,
    maxLines,
    children,
    highlightableConfig,
    ...spanProps
  } = props;
  const dt = useDesignTokens();
  const textTokens = {
    fg: disabled ? dt.stateStandardNova.disabled.fg : dt.c.colorTextDefault,
    ...(tokens && tokens({ disabled })),
    'font-size-m': dt.fontNova.size.m,
    'highlighted-bg': dt.text.highlighted.bg,
    'highlighted-fg': dt.text.highlighted.fg,
  };
  if (dt.themeVariant === 'light' && variant === 'primary-light') {
    textTokens.fg = props.disabled ? dt.statePrimaryNova.disabled.fg : dt.c.colorTextInverse;
  }

  const cssVars = renderInlineCssVars(textTokens, 'text');

  const setRef = useTruncationTooltip(ref);

  const text = useHighlightableText({
    text: typeof children === 'string' ? children : '',
    // children may be a ReactNodeArray or other types or undefined in some cases although it's declared as string.
    // Text only deals with the bidi and highlight for string type children for safety
    className: cn.highlight,
    ...highlightableConfig,
  });

  const direction = useGetDirection(children);
  const isRTL = useIsRTL();

  return (
    <span
      aria-label={ariaLabel}
      dir={props.dir ?? direction}
      tabIndex={focusable ? 0 : undefined}
      {...spanProps}
      aria-disabled={disabled}
      data-testid={testId}
      className={cx(
        cn.text,
        className,
        // the direction set to the 'dir' attribute maybe different from the locale due to BIDI characters. See useBidiFormatter for details
        // dir attribute will affect text alignment as well so set it explicitly to be consistent with locale (RTL or LTR)
        isRTL ? cn.rightAlign : cn.leftAlign,
        wrapText === false ? cn.nowrap : maxLines !== undefined && cn.hasMaxLines
      )}
      style={{
        ...cssVars,
        WebkitLineClamp: wrapText !== false ? maxLines : undefined,
        ...style,
      }}
      ref={
        (wrapText === false || maxLines !== undefined) && !preventTruncationTooltip ? setRef : ref
      }
    >
      {typeof children === 'string' ? text : children}
    </span>
  );
});
